import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Rational general function parameters: (ax + b) / (cx + d) */
export interface RationalGeneralParams {
  /** Numerator linear coefficient */
  a: number;
  /** Numerator constant */
  b: number;
  /** Denominator linear coefficient */
  c: number;
  /** Denominator constant */
  d: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for general rational functions.
 * Handle is placed at the y-intercept (0, b/d).
 */
export const RATIONAL_GENERAL_META: FunctionMeta<RationalGeneralParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'tangent',
    'secant',
    'derivative',
    'asymptotes',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'domain',
    'range',
  ],
  handleConfigs: {
    translate: {
      xParam: 'b',
      yParam: 'd',
      // Handle at y-intercept: (0, b/d)
      toPosition: (p) => [0, p['b'] / p['d']],
      // When dragged: update b to match new y-intercept (b = y * d)
      fromPosition: (_x, y, p) => ({ b: y * p['d'] }),
    },
  },
  defaultParams: {
    a: 1,
    b: 0,
    c: 1,
    d: -1,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [-10, 10],
    c: [-5, 5],
    d: [-10, 10],
  },
};
